import {
	Dispatch,
} from 'redux';
// Import services
import {
	StringService,
} from '@app/services';
// Import action type enumeration.
import ActionType from '@app/redux/actions/types';
import {
	NotificationType,
} from '@app/enumerations';
// Import api models.
import {
	MortgageeRequest,
	MortgageeRequestWithId,
	PolicyTermWithStatus,
	PublicPolicyViewData,
} from '@app/models/api';
// Import controller factory.
import ControllerFactoryProvider from '@app/controllers/factory';
// Import action methods.
import {
	addNotification,
	endProcessing,
	startProcessing,
} from '@app/redux/actions/methods';
// Import actions.
import {
	PolicyAction,
} from '@app/redux/actions';
// Import action parameters.
import {
	OverlayTargetParameters,
	PolicyParameters,
} from './ActionParametersBarrel';

interface PublicPolicyViewAction extends PolicyAction {

}

interface PublicPolicyViewParameters extends PolicyParameters {
	postalCode: string;
}

// Load Public Policy View Data Action.

type LoadPublicPolicyViewDataParameters = PublicPolicyViewParameters & OverlayTargetParameters;

interface LoadPublicPolicyViewDataAction extends PublicPolicyViewAction {
	publicPolicyViewData: PublicPolicyViewData;
}

const loadPublicPolicyViewDataAction: (publicPolicyViewData: PublicPolicyViewData, params: PublicPolicyViewParameters) =>
	LoadPublicPolicyViewDataAction = (publicPolicyViewData: PublicPolicyViewData, params: PublicPolicyViewParameters) => ({
		...params,
		publicPolicyViewData,
		type: ActionType.LOAD_PUBLIC_POLICY_VIEW_DATA,
	});

const loadPublicPolicyViewData = (params: LoadPublicPolicyViewDataParameters) =>
	async (dispatch: Dispatch): Promise<PublicPolicyViewData> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-loadPublicPolicyViewData`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Loading policy...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to load the public policy view data.
			let publicPolicyViewData: PublicPolicyViewData = await ControllerFactoryProvider.getControllerFactory().getPolicyController().getPublicPolicyViewData(params.policyId, params.postalCode);

			// Load the public policy view data to redux.
			dispatch<LoadPublicPolicyViewDataAction>(loadPublicPolicyViewDataAction(publicPolicyViewData, params));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return publicPolicyViewData;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};

// Load Public Policy Terms With Status Action.

type LoadPublicPolicyTermsWithStatusParameters = PublicPolicyViewParameters & OverlayTargetParameters;

interface LoadPublicPolicyTermsWithStatusAction extends PublicPolicyViewAction {
	publicPolicyTermsWithStatus: PolicyTermWithStatus[];
}

const loadPublicPolicyTermsWithStatusAction: (publicPolicyTermsWithStatus: PolicyTermWithStatus[], params: PublicPolicyViewParameters) =>
	LoadPublicPolicyTermsWithStatusAction = (publicPolicyTermsWithStatus: PolicyTermWithStatus[], params: PublicPolicyViewParameters) => ({
		...params,
		publicPolicyTermsWithStatus,
		type: ActionType.LOAD_PUBLIC_POLICY_TERMS_WITH_STATUS,
	});

const loadPublicPolicyTermsWithStatus = (params: LoadPublicPolicyTermsWithStatusParameters) =>
	async (dispatch: Dispatch): Promise<PolicyTermWithStatus[]> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-loadPublicPolicyTermsWithStatus`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Loading policy terms...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Call api to load the public policy terms with status.
			let policyTermsWithStatus: PolicyTermWithStatus[] = await ControllerFactoryProvider.getControllerFactory().getPolicyController().getPublicPolicyTermsWithStatus(params.policyId, params.postalCode);

			// Load the public policy terms with status to redux.
			dispatch<LoadPublicPolicyTermsWithStatusAction>(loadPublicPolicyTermsWithStatusAction(policyTermsWithStatus, params));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return policyTermsWithStatus;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};

// Update Public Policy Mortgagees Action.

interface UpdatePublicPolicyMortgageesParameters extends PublicPolicyViewParameters, OverlayTargetParameters {
	mortgageesToAdd: MortgageeRequest[];
	mortgageesToUpdate: MortgageeRequestWithId[];
	mortgageesToDelete: string[];
}

const updatePublicPolicyMortgagees = (params: UpdatePublicPolicyMortgageesParameters) =>
	async (dispatch: Dispatch): Promise<PublicPolicyViewData> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-updatePublicPolicyMortgagees`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Updating mortgagees...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			// Call api to update the mortgagees.
			await ControllerFactoryProvider.getControllerFactory().getEndorsementMortgageeController().updateMortgagees(
				params.policyId,
				params.postalCode,
				params.mortgageesToAdd,
				params.mortgageesToUpdate,
				params.mortgageesToDelete,
			);

			// Call api to load the public policy view data after updating the mortgagees.
			let publicPolicyViewData: PublicPolicyViewData = await ControllerFactoryProvider.getControllerFactory().getPolicyController().getPublicPolicyViewData(params.policyId, params.postalCode);

			// Re-load the public policy view data to redux.
			dispatch<LoadPublicPolicyViewDataAction>(loadPublicPolicyViewDataAction(publicPolicyViewData, params));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			return publicPolicyViewData;
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};


// Clear Public Policy View Data Action.

type ClearPublicPolicyViewDataParameters = PolicyParameters;

type ClearPublicPolicyViewDataAction = PolicyAction;

const clearPublicPolicyViewDataAction: (params: ClearPublicPolicyViewDataParameters) =>
	ClearPublicPolicyViewDataAction = (params: ClearPublicPolicyViewDataParameters) => ({
		...params,
		type: ActionType.CLEAR_PUBLIC_POLICY_VIEW_DATA,
	});

const clearPublicPolicyViewData = (params: ClearPublicPolicyViewDataParameters) =>
	async (dispatch: Dispatch): Promise<void> => {
		try {
			// Load the public policy view data to redux.
			dispatch<ClearPublicPolicyViewDataAction>(clearPublicPolicyViewDataAction(params));
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// Throw error;
			throw error;
		}
	};

// Export actions.
export {
	PublicPolicyViewAction,
	LoadPublicPolicyViewDataAction,
	LoadPublicPolicyTermsWithStatusAction,
	ClearPublicPolicyViewDataAction,
};

// Export action methods.
export {
	loadPublicPolicyViewData,
	loadPublicPolicyTermsWithStatus,
	updatePublicPolicyMortgagees,
	clearPublicPolicyViewData,
};

// Export action parameters.
export {
	PublicPolicyViewParameters,
	LoadPublicPolicyViewDataParameters,
	LoadPublicPolicyTermsWithStatusParameters,
	UpdatePublicPolicyMortgageesParameters,
	ClearPublicPolicyViewDataParameters,
};

// Export action factories.
export {
	loadPublicPolicyViewDataAction,
	loadPublicPolicyTermsWithStatusAction,
	clearPublicPolicyViewDataAction,
};